import { SectionData, SectionVariant } from '@betterfunnel/types';
import Modal from '@betterfunnel/ui-core/Modal';
import TextField from '@betterfunnel/ui-core/TextField';
import Toggle from '@betterfunnel/ui-core/Toggle';
import ToggleButton from '@betterfunnel/ui-core/ToggleButton';
import { DialogTitle } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { useCallback, useContext, useState } from 'react';
import { DashboardContext } from '../contexts/DashboardContext';
import { UserContext } from '../contexts/UserContext';
import {
  addSectionToAccountCatalog,
  addSectionToGlobalCatalog,
} from '../services/SectionsService';

interface AddToCatalogSectionConfig {
  name: string;
  level: 'global' | 'account';
  saveContent: boolean;
  saveImages: boolean;
}

const createDefaultAddToCatalogSectionConfig =
  (): AddToCatalogSectionConfig => ({
    name: '',
    level: 'account',
    saveContent: true,
    saveImages: true,
  });

interface Props {
  section?: SectionData;
  open: boolean;
  onClose: () => void;
}

export default function AddSectionToCatalogModal({
  section,
  open,
  onClose,
}: Props) {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const [addToCatalogSectionConfig, setAddToCatalogSectionConfig] =
    useState<AddToCatalogSectionConfig>(createDefaultAddToCatalogSectionConfig);

  const _onClose = useCallback(() => {
    setError(undefined);
    setLoading(false);
    setAddToCatalogSectionConfig(createDefaultAddToCatalogSectionConfig());
    onClose();
  }, [onClose]);

  const onAddClick = useCallback(() => {
    if (!open) return;

    if (!section || !section.type || !section.variant) {
      setError('Something went wrong. Please try again.');
      return;
    }

    if (!selectedAccount || !selectedDynamicPage) {
      setError('Something went wrong. Please try again.');
      return;
    }

    const newSection: Partial<SectionData> = {
      ...section,
    };

    if (!addToCatalogSectionConfig.saveContent) {
      delete newSection.title;
      delete newSection.tagline;
      delete newSection.subtitle;

      if ('text' in newSection) {
        delete newSection.text;
      }

      if ('description' in newSection) {
        delete newSection.description;
      }

      if ('items' in newSection) {
        delete newSection.items;
      }

      if ('features' in newSection) {
        delete newSection.features;
      }

      if ('testimonials' in newSection) {
        delete newSection.testimonials;
      }

      if ('pressMentions' in newSection) {
        delete newSection.pressMentions;
      }

      if ('logos' in newSection) {
        delete newSection.logos;
      }

      if ('cta' in newSection) {
        delete newSection.cta;
      }
    }

    if (!addToCatalogSectionConfig.saveImages) {
      if ('image' in newSection) {
        delete newSection.image;
      }
    }

    if ('formData' in newSection) {
      delete newSection.formData;
    }

    if ('formId' in newSection) {
      delete newSection.formId;
    }

    setLoading(true);
    const promise =
      addToCatalogSectionConfig.level === 'account'
        ? addSectionToAccountCatalog(
            selectedAccount.id,
            section.type,
            newSection as SectionVariant,
          )
        : addSectionToGlobalCatalog(section.type, newSection as SectionVariant);
    promise
      .then(() => {
        _onClose();
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    open,
    section,
    selectedAccount,
    selectedDynamicPage,
    addToCatalogSectionConfig.saveContent,
    addToCatalogSectionConfig.saveImages,
    addToCatalogSectionConfig.level,
    _onClose,
  ]);

  return (
    <Modal
      open={open}
      onClose={_onClose}
      actions={[
        {
          title: 'Cancel',
          type: 'secondary',
          disabled: loading,
          onClick: _onClose,
        },
        {
          title: 'Create',
          type: 'primary',
          loading: loading,
          onClick: onAddClick,
        },
      ]}
    >
      <DialogTitle
        as="h3"
        className="text-base leading-6 font-semibold text-gray-900"
      >
        Add Section to Catalog
      </DialogTitle>
      <div className="mt-4 flex flex-col gap-4">
        <TextField
          title="Name (Optional)"
          value={addToCatalogSectionConfig.name}
          onChange={(e) =>
            setAddToCatalogSectionConfig({
              ...addToCatalogSectionConfig,
              name: e.target.value,
            })
          }
        />

        <div>
          <label className="mb-2 block text-sm leading-6 font-medium text-gray-900">
            Save to
          </label>

          <ToggleButton<AddToCatalogSectionConfig['level']>
            value={addToCatalogSectionConfig.level}
            items={[
              { value: 'global', title: 'Global' },
              { value: 'account', title: 'Account' },
            ]}
            onChange={(value) =>
              setAddToCatalogSectionConfig({
                ...addToCatalogSectionConfig,
                level: value,
              })
            }
          />
        </div>

        <Toggle
          label="Save Content"
          checked={addToCatalogSectionConfig.saveContent}
          onChange={(checked) => {
            setAddToCatalogSectionConfig({
              ...addToCatalogSectionConfig,
              saveContent: checked,
            });
          }}
        />

        <Toggle
          label="Save Images"
          checked={addToCatalogSectionConfig.saveImages}
          onChange={(checked) => {
            setAddToCatalogSectionConfig({
              ...addToCatalogSectionConfig,
              saveImages: checked,
            });
          }}
        />
      </div>
      {error ? (
        <p className="mt-4 w-full text-end text-sm text-red-600">{error}</p>
      ) : (
        <></>
      )}
    </Modal>
  );
}
